import { Component, Host, h, Prop, Watch, Element, State, Event, EventEmitter } from '@stencil/core';

@Component({
  tag: 'o2-form-field',
  styleUrl: 'form-field.scss',
})
export class O2FormField {

  private formFieldId = `form-field-${formFieldId++}`;

  @Element() private el: HTMLO2FormFieldElement;

  @State() isHelpHidden: boolean = false;

  /**
   * Indicate error state of component and nested components.
   */
  @Prop({
    reflect: true,
  })
  error?: boolean = false;

  /**
   * Indicate warning state of component and nested components.
   */
  @Prop({
    reflect: true,
  })
  warning?: boolean = false;

  /**
   * Indicate disabled state of component and nested components.
   */
  @Prop({
    reflect: true,
  })
  disabled?: boolean = false;

  /**
   * Indicate readonly state of component and nested components.
   */
  @Prop({
    reflect: true,
  })
  readonly?: boolean = false;


  /**
   * Emitted when the input is ready.
   */
  @Event() o2Ready!: EventEmitter<Event>;

  /**
   * Watch change of error property.
   */
  @Watch('error')
  watchErrorHandler(newValue: boolean) {
    this.toggleError(newValue);
    // this.setAriaMessageError();
  }

  /**
   * Watch change of warning property.
   */
  @Watch('warning')
  watchWarningHandler(newValue: boolean) {
    this.toggleWarning(newValue);
  }

  /**
   * Watch change of disabled property.
   */
  @Watch('disabled')
  watchDisabledHandler(newValue: boolean) {
    this.toggleDisabled(newValue);
  }

  /**
   * Watch change of readonly property.
   */
  @Watch('readonly')
  watchReadonlyHandler(newValue: boolean) {
    this.toggleReadonly(newValue);
  }

  /**
   * If state is changed from outside, set it also to nested components.
   */
  private toggleError(value: boolean) {
    const tag = this.getO2ElementName();

    if (tag !== 'o2-checkbox' && tag !== 'o2-toggle') {
      let o2Element: any = this.el.querySelector(this.getO2ElementName());
      if (o2Element) {
        o2Element.error = value;
      }
    }
  }

  private setHelpMarginForSelectable() {
    const tag = this.getO2ElementName();

    if (tag == 'o2-checkbox' || tag == 'o2-radio-group') {
      if (this.el.querySelector('.help')) this.el.querySelector('.help').classList.add('help--selectable');
    }
  }

  /**
   * If state is changed from outside, set it also to nested components.
   */
  private toggleWarning(value: boolean) {
    let o2Element: any = this.el.querySelector(this.getO2ElementName());
    if (o2Element) {
      o2Element.warning = value;
    }
  }

  /**
   * If state is changed from outside, set it also to nested components.
   */
  private toggleDisabled(value: boolean) {
    let o2Element: any = this.el.querySelector(this.getO2ElementName());
    if (o2Element) {
      o2Element.disabled = value;
    }
  }

  /**
   * If state is changed from outside, set it also to nested components.
   */
  private toggleReadonly(value: boolean) {
    let o2Element: any = this.el.querySelector(this.getO2ElementName());
    if (o2Element) {
      o2Element.readonly = value;
    }
  }

  /**
   * Get name of slotted form element (e.g. o2-input).
   */
  private getO2ElementName(): string {
    const possibleTags = [
      'o2-input',
      'o2-password',
      'o2-password-indicator',
      'o2-textarea',
      'o2-checkbox',
      'o2-checkbox-group',
      'o2-radio-group',
      'o2-select',
      'o2-select-single',
      'o2-select-multiple',
      'o2-datetime',
      'o2-msisdn-selector',
      'o2-search',
      'input',
      'textarea',
    ];
  
    // Use querySelectorAll to find all matching elements
    const matchingElements = Array.from(this.el.querySelectorAll('*')).filter(el =>
      possibleTags.includes(el.tagName.toLowerCase())
    );
  
    // Return the first matching tag name, or undefined if none found
    return matchingElements.length > 0 ? matchingElements[0].tagName.toLowerCase() : undefined;
  }

  connectedCallback() {
    this.watchHelpTextChange();

    if (this.error) {
      this.toggleError(true);
    }

    if (this.warning) {
      this.toggleWarning(true);
    }

    if (this.disabled) {
      this.toggleDisabled(true);
    }

    if (this.readonly) {
      this.toggleReadonly(true);
    }
  }

  // Create a MutationObserver to listen for changes in the span element
  private watchHelpTextChange() {
    const helpTextElement = this.el.querySelector('[slot="help-text"]');

    if (helpTextElement) {
      this.setHelpVisibility(helpTextElement);

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            this.setHelpVisibility(helpTextElement);
          }
        });
      });

      observer.observe(helpTextElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  private setHelpVisibility(helpText: Element) {
    this.isHelpHidden = helpText.textContent.trim() === '';
  }

  componentWillLoad() {
    // set data-form-field-id to form component to connect with help-text
    const o2Elements: any = this.el.querySelectorAll('o2-input, o2-password, o2-password-indicator, o2-textarea, o2-checkbox, o2-checkbox-group, o2-radio-group, o2-select, o2-select-single, o2-select-multiple, o2-datetime, o2-msisdn-selector, o2-search, input, textarea');
    o2Elements.forEach(o2Element => {
      o2Element.dataset.formFieldId = this.formFieldId;
    });
  }

  componentDidRender() {
    this.o2Ready.emit();
  }

  componentDidLoad() {
    this.setHelpMarginForSelectable();
  }

  render() {
    return (
      <Host>
        <slot></slot>

        <div class={`help ${this.isHelpHidden ? 'help--hidden' : ''} ${this.disabled ? 'help--disabled' : ''} ${this.readonly ? 'help--readonly' : ''}`} aria-live="polite">
          <span id={`${this.formFieldId}-help`} class={`help__text ${this.error ? 'help__text--error' : ''} ${this.warning ? 'help__text--warning' : ''}`}>
            <slot name="help-text"></slot>
          </span>
        </div>

        <slot name="password-indicator"></slot>
      </Host>
    );
  }
}

let formFieldId = 0;